import { useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@context/contexts";
import { useApi } from "@hooks/useApi";
import PropTypes from 'prop-types';
import { todayIso, formatIsoDate } from "@utils/index";
import YearHeatmap from "@components/admin-components/YearHeatmap";

const cellTitle = (iso, entry) => {
    const { count = 0, total = 0 } = entry ?? {};
    const share = total > 0 ? `${((count / total) * 100).toFixed(2)}%` : "-";
    return `${formatIsoDate(iso)}: ${count}/${total} (${share}) задач`;
};

const getColor = (count, date) => {
    const today = todayIso();
    if (date > today) return "#a3a3a386";

    if (count === 0) return "#ffffffff";
    if (count === 1) return "#c6e48b";
    if (count < 5) return "#83d776ff";
    if (count < 10) return "#239a3b";
    if (count < 25) return "#0f7624ff";
    if (count < 50) return "#0c541bff";
    if (count < 100) return "#073813ff";
    return "#ced108ff";
};

const TaskHeatmap = ({ lichessId = null }) => {
    const { token } = useUser();
    const api = useApi();

    const { data: dailyStats = [], isError } = useQuery({
        queryKey: ["puzzle-daily", lichessId || "me", token],
        queryFn: async () => {
            const endpoint = lichessId ? `puzzle/daily/${lichessId}` : "puzzle/daily";
            const result = await api.get(endpoint);
            if (!result?.ok || !Array.isArray(result.data)) {
                throw new Error("Некорректный ответ сервера");
            }
            return result.data;
        },
        enabled: Boolean(token),
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        if (isError) {
            toast.error("Не удалось загрузить статистику");
        }
    }, [isError]);

    const { statistics, allDates } = useMemo(() => {
        const mapped = {};
        const dates = [];
        for (const item of dailyStats) {
            const { date, count, total } = item;
            mapped[date] = { count, total };
            dates.push(date);
        }
        return { statistics: mapped, allDates: dates };
    }, [dailyStats]);

    const getCellTitle = useCallback(iso => cellTitle(iso, statistics[iso]), [statistics]);
    const getCellStyle = useCallback((iso) => {
        const count = statistics[iso]?.count ?? 0;
        return {
            backgroundColor: getColor(count, iso),
            border: count === 0 ? "1px solid #ccc" : "none",
        };
    }, [statistics]);

    return (
        <YearHeatmap
            title={lichessId ? `Активность игрока ${lichessId}` : "Активность по решённым задачам"}
            dates={allDates}
            getCellTitle={getCellTitle}
            getCellStyle={getCellStyle}
        />
    );
};

TaskHeatmap.propTypes = {
    lichessId: PropTypes.string
};

export default TaskHeatmap;

